import { ReactIcon } from '@/components/react/ui/ReactIcon';
import { ICON_COLORS, type IconName } from '@/consts';
import { cva, cx, type VariantProps } from '@/cva.config';
import type { HTMLAttributes } from 'react';

export const badgeVariants = cva({
  base: 'inline-flex justify-center items-center gap-1 px-2 py-1',
  variants: {
    color: {
      gray: 'bg-blurb-bg-gray-subtler text-blurb-text-bold',
      bold: 'bg-blurb-bg-gray-bold text-blurb-text-inverse',
      unset: null,
    },
    size: {
      large: 'text-xs md:text-sm gap-1 pr-4',
      small: 'text-xs gap-0.5',
    },
    style: {
      round: 'rounded-full',
      square: 'rounded-sm',
      unset: null,
    },
    weight: {
      semibold: 'font-semibold',
      unset: null,
    },
  },
});

export type BadgeProps = HTMLAttributes<HTMLDivElement>
  & VariantProps<typeof badgeVariants> & {
    icon?: IconName;
    text: string;
    customBackgroundColor?: string;
    customTextColor?: keyof typeof ICON_COLORS;
  };

export const ReactBadge = ({
  className,
  color = 'gray',
  customBackgroundColor,
  customTextColor,
  icon,
  size = 'large',
  style = 'round',
  text,
  weight = 'unset',
  ...props
}: BadgeProps) => {
  const textColor =
    customTextColor && ICON_COLORS[customTextColor] ?
      ICON_COLORS[customTextColor]
    : customTextColor;

  return (
    <div
      className={cx(
        badgeVariants({ color, size, style, weight }),
        customBackgroundColor,
        !icon && size === 'large' && 'pl-4',
        className,
      )}
      {...props}
    >
      {icon && <ReactIcon name={icon} size={size} className={textColor} />}
      <span
        title={text}
        className={cx(textColor, 'overflow-hidden text-ellipsis')}
      >
        {text}
      </span>
    </div>
  );
};
